import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Users, Ticket, Pulse, Database, Warning, Plus, Copy, Trash } from "@phosphor-icons/react";
import { AppHeader } from "../components/AppHeader";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import api, { formatApiError } from "../lib/api";
import { fmtDate, timeAgo } from "../lib/format";

function StatCard({ icon: Icon, label, value, sub, tone, testid }) {
  return (
    <div className="border border-border bg-card p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground mb-3">
        <Icon size={16} weight="bold" />
        <span className="text-[10px] uppercase tracking-[0.2em]">{label}</span>
      </div>
      <div className={`font-mono text-2xl font-bold tracking-tight ${tone || ""}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function Overview({ stats }) {
  const md = stats.market_data;
  const badge = (ok) => ok === true ? <span className="text-success">Connected</span> : ok === false ? <span className="text-danger">Error</span> : <span className="text-muted-foreground">Unknown</span>;
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Users} label="Active Users" value={`${stats.users.active} / ${stats.users.max}`} sub={`${stats.users.available} slots available`} testid="stat-users" />
        <StatCard icon={Ticket} label="Active Invites" value={stats.invitations.active} sub={`${stats.invitations.used} used · ${stats.invitations.revoked} revoked`} testid="stat-invites" />
        <StatCard icon={Pulse} label="Currency API" value={badge(md.currency.ok)} sub={md.currency.checked_at ? `Checked ${timeAgo(md.currency.checked_at)}` : "—"} testid="stat-currency-api" />
        <StatCard icon={Pulse} label="Crypto API" value={badge(md.crypto.ok)} sub={md.crypto.checked_at ? `Checked ${timeAgo(md.crypto.checked_at)}` : "—"} testid="stat-crypto-api" />
      </div>
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="border border-border bg-card p-5">
          <div className="flex items-center gap-2 text-muted-foreground mb-3"><Database size={16} weight="bold" /><span className="text-[10px] uppercase tracking-[0.2em]">Cache Status</span></div>
          <div className="space-y-2 text-sm font-mono">
            <div className="flex justify-between"><span className="text-muted-foreground">Currency cache</span><span>{stats.cache.currency_updated ? timeAgo(stats.cache.currency_updated) : "—"}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Crypto cache</span><span>{stats.cache.crypto_updated ? timeAgo(stats.cache.crypto_updated) : "—"}</span></div>
          </div>
        </div>
        <div className="border border-border bg-card p-5">
          <div className="flex items-center gap-2 text-muted-foreground mb-3"><Warning size={16} weight="bold" /><span className="text-[10px] uppercase tracking-[0.2em]">Recent API Errors</span></div>
          {stats.errors.length === 0 ? <p className="text-sm text-muted-foreground">No recent errors.</p> : (
            <div className="space-y-2 max-h-40 overflow-auto" data-testid="admin-errors">
              {stats.errors.map((e, i) => (
                <div key={i} className="text-xs font-mono border-l-2 border-danger pl-2">
                  <span className="text-danger uppercase">{e.source}</span> · <span className="text-muted-foreground">{timeAgo(e.at)}</span>
                  <div className="text-muted-foreground truncate">{e.message}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function UsersPanel() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["admin-users", search],
    queryFn: async () => (await api.get("/admin/users", { params: { search } })).data.users,
  });
  const status = useMutation({
    mutationFn: async ({ id, status }) => (await api.patch(`/admin/users/${id}/status`, { status })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-users"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/users/${id}`)).data,
    onSuccess: () => { toast.success("User deleted"); qc.invalidateQueries({ queryKey: ["admin-users"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const users = data || [];

  return (
    <div className="border border-border bg-card">
      <div className="p-4 border-b border-border">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search users by email…" data-testid="admin-user-search" className="rounded-none max-w-sm" />
      </div>
      {isLoading ? <Skeleton className="h-40 rounded-none m-4" /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                <th className="text-left font-medium px-4 py-2">Email</th>
                <th className="text-left font-medium px-4 py-2 hidden sm:table-cell">Created</th>
                <th className="text-left font-medium px-4 py-2 hidden md:table-cell">Last Login</th>
                <th className="text-left font-medium px-4 py-2">Status</th>
                <th className="text-right font-medium px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {users.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">No users found.</td></tr>
              ) : users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.email}`} className="hover:bg-accent/50">
                  <td className="px-4 py-3 font-mono">{u.email}</td>
                  <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">{fmtDate(u.created_at)}</td>
                  <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{u.last_login ? timeAgo(u.last_login) : "Never"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${u.status === "active" ? "border-success/40 text-success" : "border-danger/40 text-danger"}`}>{u.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => status.mutate({ id: u.id, status: u.status === "active" ? "disabled" : "active" })} data-testid={`toggle-${u.email}`} className="text-xs border border-border px-2 py-1 hover-lift mr-2">
                      {u.status === "active" ? "Disable" : "Enable"}
                    </button>
                    <button onClick={() => { if (window.confirm(`Delete ${u.email}?`)) del.mutate(u.id); }} data-testid={`delete-${u.email}`} className="text-xs border border-danger/40 text-danger px-2 py-1 hover-lift">
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function InvitesPanel() {
  const qc = useQueryClient();
  const [count, setCount] = useState(1);
  const [maxUses, setMaxUses] = useState(1);
  const [expDays, setExpDays] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["admin-invites"],
    queryFn: async () => (await api.get("/admin/invitations")).data.invitations,
  });
  const gen = useMutation({
    mutationFn: async () => (await api.post("/admin/invitations", {
      count: Number(count), max_uses: Number(maxUses),
      expires_in_days: expDays ? Number(expDays) : null,
    })).data,
    onSuccess: (d) => { toast.success(`${d.created.length} code(s) generated`); qc.invalidateQueries({ queryKey: ["admin-invites"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const revoke = useMutation({
    mutationFn: async (id) => (await api.post(`/admin/invitations/${id}/revoke`)).data,
    onSuccess: () => { toast.success("Code revoked"); qc.invalidateQueries({ queryKey: ["admin-invites"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const invites = data || [];
  const inactiveCount = invites.filter((i) => i.state !== "active").length;
  const cleanup = useMutation({
    mutationFn: async () => (await api.post("/admin/invitations/cleanup")).data,
    onSuccess: (d) => { toast.success(`Removed ${d.deleted} inactive code${d.deleted === 1 ? "" : "s"}`); qc.invalidateQueries({ queryKey: ["admin-invites"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/invitations/${id}`)).data,
    onSuccess: () => { toast.success("Code deleted"); qc.invalidateQueries({ queryKey: ["admin-invites"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const stateColor = { active: "text-success border-success/40", used: "text-neutral border-border", expired: "text-amber-500 border-amber-500/40", revoked: "text-danger border-danger/40" };

  return (
    <div className="space-y-4">
      <div className="border border-border bg-card p-5">
        <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-4">Generate Invitation Codes</h3>
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="inv-quick">
          <span className="text-xs text-muted-foreground mr-1">Quick:</span>
          {[10, 25, 50, 100].map((n) => (
            <button key={n} type="button" onClick={() => setCount(n)} data-testid={`inv-quick-${n}`}
              className={`text-xs border px-3 py-1.5 font-mono hover-lift ${Number(count) === n ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`}>
              {n}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1"><Label className="text-xs">Count</Label><Input type="number" min={1} max={100} value={count} onChange={(e) => setCount(e.target.value)} data-testid="inv-count" className="rounded-none w-24" /></div>
          <div className="space-y-1"><Label className="text-xs">Max uses each</Label><Input type="number" min={1} value={maxUses} onChange={(e) => setMaxUses(e.target.value)} data-testid="inv-maxuses" className="rounded-none w-28" /></div>
          <div className="space-y-1"><Label className="text-xs">Expires in days (optional)</Label><Input type="number" min={1} value={expDays} onChange={(e) => setExpDays(e.target.value)} data-testid="inv-expiry" className="rounded-none w-40" placeholder="Never" /></div>
          <Button onClick={() => gen.mutate()} disabled={gen.isPending} className="rounded-none" data-testid="inv-generate"><Plus size={16} className="mr-1" /> Generate</Button>
        </div>
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border">
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground">Invitation Codes</span>
          <button
            onClick={() => { if (window.confirm(`Delete all ${inactiveCount} inactive (used/expired/revoked) code(s)? This cannot be undone.`)) cleanup.mutate(); }}
            disabled={inactiveCount === 0 || cleanup.isPending}
            data-testid="inv-cleanup-btn"
            className="inline-flex items-center gap-1.5 text-xs border border-danger/40 text-danger px-3 py-1.5 hover-lift disabled:opacity-40 disabled:cursor-not-allowed">
            <Trash size={14} /> Clear Inactive{inactiveCount ? ` (${inactiveCount})` : ""}
          </button>
        </div>
        {isLoading ? <Skeleton className="h-40 rounded-none m-4" /> : (
          <table className="w-full text-sm" data-testid="admin-invites-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                <th className="text-left font-medium px-4 py-2">Code</th>
                <th className="text-left font-medium px-4 py-2">State</th>
                <th className="text-left font-medium px-4 py-2 hidden sm:table-cell">Uses</th>
                <th className="text-left font-medium px-4 py-2 hidden md:table-cell">Used By</th>
                <th className="text-left font-medium px-4 py-2 hidden lg:table-cell">Expires</th>
                <th className="text-right font-medium px-4 py-2">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {invites.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No invitation codes yet.</td></tr>
              ) : invites.map((inv) => (
                <tr key={inv.id} data-testid={`invite-row-${inv.code}`} className="hover:bg-accent/50">
                  <td className="px-4 py-3 font-mono flex items-center gap-2">
                    {inv.code}
                    <button onClick={() => { navigator.clipboard?.writeText(inv.code); toast.success("Copied"); }} className="text-muted-foreground hover:text-foreground" data-testid={`copy-${inv.code}`}><Copy size={14} /></button>
                  </td>
                  <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${stateColor[inv.state]}`}>{inv.state}</span></td>
                  <td className="px-4 py-3 font-mono hidden sm:table-cell">{inv.uses_remaining}/{inv.max_uses}</td>
                  <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{inv.used_by || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground hidden lg:table-cell">{inv.expires_at ? fmtDate(inv.expires_at) : "Never"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {inv.state === "active"
                      ? <button onClick={() => revoke.mutate(inv.id)} data-testid={`revoke-${inv.code}`} className="text-xs border border-danger/40 text-danger px-2 py-1 hover-lift">Revoke</button>
                      : <button onClick={() => del.mutate(inv.id)} data-testid={`delete-inv-${inv.code}`} className="text-xs border border-border text-muted-foreground px-2 py-1 hover-lift inline-flex items-center gap-1"><Trash size={12} /> Delete</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const { data: stats, isLoading } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: async () => (await api.get("/admin/stats")).data,
    refetchInterval: 30000,
  });

  const tabs = [["overview", "Overview"], ["users", "Users"], ["invites", "Invitations"]];

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mb-1">Admin Dashboard</h1>
        <p className="text-sm text-muted-foreground mb-8">Maria's Forex / Crypto administration</p>

        <div className="inline-flex border border-border mb-6" data-testid="admin-tabs">
          {tabs.map(([v, l], i) => (
            <button key={v} onClick={() => setTab(v)} data-testid={`admin-tab-${v}`}
              className={`px-5 py-2.5 text-sm font-bold uppercase tracking-wider transition-colors ${i > 0 ? "border-l border-border" : ""} ${tab === v ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
          ))}
        </div>

        {isLoading || !stats ? <Skeleton className="h-40 rounded-none" /> : (
          <>
            {tab === "overview" && <Overview stats={stats} />}
            {tab === "users" && <UsersPanel />}
            {tab === "invites" && <InvitesPanel />}
          </>
        )}
      </main>
    </div>
  );
}
